import { useCallback, useRef } from 'react';
import { useSearchState } from './useSearchState';
import { useSearchOperations } from './useSearchOperations';
import { useSearchKeyboard } from './useSearchKeyboard';
import type { UseUnifiedSearchProps, UseUnifiedSearchReturn } from './types/useUnifiedSearchTypes';

export function useUnifiedSearch({
  onOpenMovieDetails,
  onExplicitClose
}: UseUnifiedSearchProps = {}): UseUnifiedSearchReturn {
  const {
    query,
    setQuery,
    results,
    setResults,
    isOpen,
    setIsOpen,
    isLoading,
    setIsLoading,
    error,
    setError,
    selectedIndex,
    setSelectedIndex,
    hasSearchCompleted,
    setHasSearchCompleted,
    clearAll: stateClearAll,
    resetResults
  } = useSearchState();

  const searchResultsRef = useRef<HTMLDivElement>(null);

  const { abortCurrentSearch, performDirectSearch } = useSearchOperations({
    query,
    setResults,
    setIsLoading,
    setError,
    setIsOpen,
    resetResults,
    setHasSearchCompleted
  });

  const { handleKeyDown, triggerDirectSearch } = useSearchKeyboard({
    query,
    isOpen,
    totalHits: results.total,
    selectedIndex,
    setSelectedIndex,
    setIsOpen,
    onExplicitClose,
    performDirectSearch
  });

  // Handle input changes
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setSelectedIndex(-1);
    setHasSearchCompleted(false); // Reset when query changes
    
    // Open results when user starts typing
    if (newQuery.trim().length >= 2) {
      setIsOpen(true);
    }
  }, [setQuery, setSelectedIndex, setIsOpen, setHasSearchCompleted]);

  // Close results but keep query (for outside clicks)
  const closeResults = useCallback(() => {
    abortCurrentSearch();
    setIsOpen(false);
    setSelectedIndex(-1);
    setIsLoading(false);
    setError(null);
  }, [abortCurrentSearch, setIsOpen, setSelectedIndex, setIsLoading, setError]);

  // Clear everything - the single source of truth for clearing
  const clearAll = useCallback(() => {
    abortCurrentSearch();
    stateClearAll();
  }, [abortCurrentSearch, stateClearAll]);

  // Enhanced direct search that uses the operations hook
  const enhancedDirectSearch = useCallback(() => {
    performDirectSearch();
  }, [performDirectSearch]);

  return {
    query,
    handleInputChange,
    isResultsOpen: isOpen,
    setIsResultsOpen: setIsOpen,
    categorizedResults: results.items,
    totalHits: results.total,
    isSearching: isLoading,
    showNoResultsMessage: isOpen && !isLoading && results.total === 0 && query.length >= 2 && hasSearchCompleted,
    searchError: error,
    handleKeyDown,
    closeResults,
    clearAll,
    triggerDirectSearch: enhancedDirectSearch,
    searchResultsRef
  };
}
